<template>
  <Card>
    <template #content>
      <div class="flex flex-col gap-8">
        <FormGroup :label="$t('forms.device_revision.device_revision_properties_form.name')"
                   :error-text="form.errors?.name">
          <InputText
              data-cy="device-revision-input-value"
              ref="name"
              v-model="form.name"
              placeholder="e.g. AB23XYZ"
              @change="form.validate('name')"
              @keyup="onNameChange"
          />
        </FormGroup>

        <FormGroup :label="$t('forms.device_revision.device_revision_properties_form.key')"
                   :error-text="form.errors?.key">
          <InputText
              v-model="form.key"
              @change="form.validate('key')"
              :disabled="true"
          />
        </FormGroup>

        <FormGroup :label="$t('forms.device_revision.device_revision_properties_form.device_model')"
                   :error-text="form.errors.device_model_id">
          <Dropdown
              data-cy="select-device-model"
              v-model="form.device_model_id"
              :options="deviceModels"
              option-label="name"
              option-value="id"
              @change="form.validate('device_model_id')"
          />
        </FormGroup>

        <div v-if="isTelematicsModel" class="flex items-center gap-6">
          <div class="flex items-center gap-2">
            <Checkbox v-model="form.is_usd" :binary="true" inputId="is_usd" @change="form.validate('is_usd')" />
            <label for="is_usd" class="text-sm font-medium leading-6 text-surface-900 dark:text-surface-200 cursor-pointer">
              {{ $t('forms.device_revision.device_revision_properties_form.is_usd') }}
            </label>
          </div>
          <div class="flex items-center gap-2">
            <Checkbox v-model="form.is_5v_can" :binary="true" inputId="is_5v_can" @change="form.validate('is_5v_can')" />
            <label for="is_5v_can" class="text-sm font-medium leading-6 text-surface-900 dark:text-surface-200 cursor-pointer">
              {{ $t('forms.device_revision.device_revision_properties_form.is_5v_can') }}
            </label>
          </div>
        </div>

        <FormGroup :label="$t('forms.device_revision.device_revision_properties_form.description')"
                   :error-text="form.errors?.description">
					<Textarea
              ref="description"
              v-model="form.description"
              @change="form.validate('description')"
              rows="5"
          />
        </FormGroup>

        <FormGroup :label="$t('forms.device_revision.device_revision_properties_form.relevant_regulations')">
          <MultiSelect 
                       data-cy="select-relevant-regulation"
                       v-model="form.regulation_ids"
                       :options="regulationsStore.getRegulationOptions"
                       optionLabel="name"
                       :placeholder="$t('forms.device_revision.device_revision_properties_form.select_regulations')"
                       display="chip"
                       class="w-full md:w-20rem"
                       option-value="id">
            <template #option="slotProps">
              <div class="flex align-items-center">
                <div>{{ slotProps.option.name }} - <span class="font-light">{{ slotProps.option.description }}</span>
                </div>
              </div>
            </template>
            <template #footer>
              <div class="py-2 px-3">
                {{
                  $t('forms.common.x_items_selected', form.regulation_ids?.length ?? 0, {count: form.regulation_ids?.length ?? 0})
                }}
              </div>
            </template>
          </MultiSelect>
        </FormGroup>

        <FormGroup :label="$t('forms.device_revision.device_revision_properties_form.vehicle_manufacturers')">
          <MultiSelect
              data-cy="select-vehicle-manufacturer"
              v-model="form.vehicle_manufacturer_ids"
              :options="companiesStore.getVehicleManufacturerOptions"
              :placeholder="$t('forms.device_revision.device_revision_properties_form.select_vehicle_manufacturers')"
              option-label="name"
              option-value="id"
              display="chip"
              class="w-full md:w-20rem"
          >
            <template #footer>
              <div class="py-2 px-3">
                {{
                  $t('forms.common.x_items_selected', form.vehicle_manufacturer_ids?.length ?? 0, {count: form.vehicle_manufacturer_ids?.length ?? 0})
                }}
              </div>
            </template>
          </MultiSelect>
        </FormGroup>

      </div>
    </template>

    <template v-if="hasOnSaveListener || hasOnDeleteListener" #footer>
      <div class="flex justify-end space-x-3">
        <Button data-cy="device-revision-delete-btn" class="px-8" text severity="danger" v-if="hasOnDeleteListener && form.id" @click="$emit('on-delete', form)"
                :label="$t('forms.common.delete')"
                :disabled="form.processing"/>
        <Button data-cy="device-revision-save-btn" class="px-8" v-if="hasOnSaveListener" @click="$emit('on-save', form)" :label="$t('forms.common.save')"
                :disabled="form.processing"/>
      </div>
    </template>
  </Card>
</template>

<script>
import {mapStores} from "pinia";
import FormGroup from "@/Components/FormGroup.vue";
import {useCompaniesStore} from "@/stores/companies.js";
import {useDeviceModelsStore} from "@/stores/deviceModels.js";
import {useDeviceTypesStore} from "@/stores/deviceTypes.js";
import {useRegulationsStore} from "@/stores/regulations.js";

export default {
  name: "DeviceRevisionsPropertiesForm",
  components: {FormGroup},
  props: {
    form: {
      type: Object,
      required: true,
    },
  },
  computed: {
    ...mapStores(useCompaniesStore),
    ...mapStores(useDeviceModelsStore),
    ...mapStores(useDeviceTypesStore),
    ...mapStores(useRegulationsStore),
    hasOnSaveListener() {
      return !!(this.$attrs?.onOnSave);
    },
    hasOnDeleteListener() {
      return !!(this.$attrs?.onOnDelete);
    },
    deviceModels() {
      return this.deviceModelsStore.getDeviceModelOptions || [];
    },
    isTelematicsModel() {
      if (!this.form.device_model_id) return false;
      const model = this.deviceModels.find(m => m.id === this.form.device_model_id);
      if (!model?.device_type_id) return false;
      const type = (this.deviceTypesStore.getDeviceTypeOptions || []).find(t => t.id === model.device_type_id);
      return type?.name?.toLowerCase() === 'telematics';
    },
  },
  methods: {
    onNameChange($event) {
      this.form.key = this.form.name?.trim().replaceAll(' ', '_').toUpperCase();
      this.form.validate('key');
    }
  },
  mounted() {
    this.companiesStore.indexCompanyOptions();
    this.deviceModelsStore.indexDeviceModelOptions();
    this.deviceTypesStore.indexDeviceTypeOptions();
    this.regulationsStore.indexRegulationOptions();

    this.$refs.name.$el.focus();
  }
}
</script>

<style scoped>

</style>
